import React, { useState, useRef, useEffect } from 'react'
import 'react-dates/initialize'
import { DateRangePickerShape, isInclusivelyBeforeDay } from 'react-dates'
import { StyledDateRangePicker } from 'components/common/DatePickersStyled'
import moment, { isMoment } from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// This is an overwrite to avoid exposing all of moment to the app
// this is the necessary attrs for DateRangePicker to work
interface DateRangePickerDates {
  // overwrite moment with Dates
  startDate: Date | null
  endDate: Date | null
  onDatesChange: (
    arg: {
      startDate: Date | null
      endDate: Date | null
    }
  ) => void
  onClose?: (
    final: {
      startDate: Date | null
      endDate: Date | null
    }
  ) => void;
  initialVisibleMonth?: () => Date
  minDate?: Date
  maxDate?: Date

  // custom attrs
  disableFutureDates?: Boolean
  hideInputs?: Boolean
}

// ignore a few other momentjs funcs, if needed, add to DateRangePickerDates
type IgnoredProps = 'renderMonthText' | 'renderMonthElement' | 'renderCalendarDay' |
'renderDayContents' | 'onPrevMonthClick' | 'onNextMonthClick' |
'isDayBlocked' | 'isOutsideRange' | 'isDayHighlighted'

// reactdate's DateRangePickerShape without
// - DateRangePickerDates to prevent type conflicts from moment => Date
// - IgnoredProps to prevent exposing moment
interface DateRangePickerShapeFiltered extends Omit<DateRangePickerShape, keyof DateRangePickerDates | IgnoredProps> { }

// essentially a de-moment'ized version DateRangePickerShape
export interface DateRangePickerProps extends DateRangePickerShapeFiltered, DateRangePickerDates { }

// convert incoming Date to moment
export function toMoment<T = Date | null | undefined>(d: T): Exclude<T, Date> | moment.Moment {
  return (d ? moment(d) : d) as Exclude<T, Date> | moment.Moment
}

// convert outgoing moment to Date
export function toDate<T = moment.Moment | null | undefined>(d: T): Exclude<T, moment.Moment> | Date {
  return (isMoment(d) ? d.toDate() : d) as Exclude<T, moment.Moment> | Date
}

export const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    startDate,
    endDate,
    minDate,
    maxDate,
    disableFutureDates,
    onDatesChange,
    onClose,
    initialVisibleMonth,
    hideInputs,
    ...rest
  } = props

  const classes = []

  if (hideInputs) {
    classes.push('hideInputs')
  }

  const newProps: DateRangePickerShape = {
    ...rest,
    startDate: toMoment(startDate),
    endDate: toMoment(endDate),
    minDate: toMoment(minDate),
    maxDate: toMoment(maxDate),
    onDatesChange: (arg) => {
      onDatesChange({
        startDate: toDate(arg.startDate),
        endDate: toDate(arg.endDate),
      })
    },
    onClose: onClose ? (final) => {
      onClose({
        startDate: toDate(final.startDate),
        endDate: toDate(final.endDate),
      })
    } : undefined,
    initialVisibleMonth: initialVisibleMonth ? () => toMoment(initialVisibleMonth()) : undefined,
  }

  if (disableFutureDates) {
    const tomorrow = moment().add(1, 'day')
    newProps.isOutsideRange = (day: any) => isInclusivelyBeforeDay(tomorrow, day)
  }

  return <StyledDateRangePicker className={classes.join(' ')} {...newProps} />
}

export const DefaultDateRangePicker = (props: Partial<DateRangePickerProps>) => {
  const startDateId = useRef(Math.random().toString(20))
  const endDateId = useRef(Math.random().toString(20))

  const {
    startDate, endDate, onDatesChange, ...rest
  } = props
  const [focusedInput, setFocusedInput] = useState<DateRangePickerProps['focusedInput']>(props.focusedInput || null)
  const [dates, setDates] = useState<Array<Date | null>>([startDate || null, endDate || null])

  useEffect(() => {
    setFocusedInput((current) => {
      if (current !== props.focusedInput) {
        return props.focusedInput || null
      }
      return current
    })
  }, [props.focusedInput, setFocusedInput])

  return (
    <DateRangePicker
      {...rest}
      disableFutureDates={props.disableFutureDates !== undefined ? props.disableFutureDates : true}
      showClearDates={props.showClearDates !== undefined ? props.showClearDates : true}
      hideKeyboardShortcutsPanel={true}
      minimumNights={0}
      focusedInput={focusedInput}
      onFocusChange={(input) => { setFocusedInput(input) }}
      startDate={dates[0]}
      startDateId={startDateId.current}
      endDate={dates[1]}
      endDateId={endDateId.current}
      customInputIcon={<FontAwesomeIcon icon={['far', 'calendar']} fontSize="10" />}
      onDatesChange={({ startDate, endDate }) => {
        if (onDatesChange) {
          onDatesChange({ startDate, endDate })
        }
        // handle the clear dates button
        if (!focusedInput && rest.onClose) {
          rest.onClose({ startDate, endDate })
        }
        return setDates([startDate, endDate])
      }}
    />
  )
}
